import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useQuery } from '@tanstack/react-query';

import { CopyIcon, Cross2Icon, DownloadIcon } from '@radix-ui/react-icons';

import { MediaGraphQLService } from '@/shared/api/media-graphql-service';
import { logger } from '@/shared/lib/logger';
import { useMediaInferenceViewerStore } from '@/shared/stores/media-inference-viewer-store';
import { useSelectedMediaStore } from '@/shared/stores/selected-media-store';
import { useSelectedPersonaStore } from '@/shared/stores/selected-persona-store';
import { ModalPortal } from '@/shared/ui';

export const MediaInferenceViewerModal = () => {
  const { isOpen, mediaData, inferenceId, closeMedia } = useMediaInferenceViewerStore();
  const setSelectedPersona = useSelectedPersonaStore((state) => state.setSelectedPersona);
  const setSelectedImage = useSelectedMediaStore((state) => state.setSelectedImage);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  // Загрузка данных inference
  const { data: inferenceData, isLoading: isLoadingInference } = useQuery({
    queryKey: ['inference', inferenceId],
    queryFn: async () => {
      if (!inferenceId) return null;
      try {
        const response = await Promise.race([
          MediaGraphQLService.getInference(Number(inferenceId)),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Inference fetch timeout')), 5000)
          ),
        ]);
        return response.data?.inference ?? null;
      } catch (error) {
        logger.inference.warn('Failed to fetch inference prompt', { inferenceId, error });
        return null;
      }
    },
    enabled: !!inferenceId && isOpen,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });

  // Определение типа медиа
  const getMediaType = (extension?: string): 'image' | 'video' => {
    if (!extension) return 'image';
    const ext = extension.toLowerCase();
    const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'flv', 'wmv', 'm4v'];
    return videoExtensions.includes(ext) ? 'video' : 'image';
  };

  const mediaType = getMediaType(mediaData?.extension);
  // Try inputPrompt from inference, fall back to displayName from mediaInference
  const prompt = inferenceData?.inputPrompt || mediaData?.displayName || null;

  const handleCopyPrompt = useCallback(async () => {
    if (!prompt) return;

    try {
      await navigator.clipboard.writeText(prompt);
      toast.success('Prompt copied to clipboard!');
    } catch (error) {
      logger.ui.error('Failed to copy prompt', { error });
      toast.error('Failed to copy prompt');
    }
  }, [prompt]);

  const handleDownload = useCallback(async () => {
    if (!mediaData?.url) return;

    try {
      const response = await fetch(mediaData.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = mediaData.extension || 'jpg';
      a.download = `${mediaData.name || 'media'}-${Date.now()}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.ui.error('Failed to download media', { error, mediaUrl: mediaData?.url });
      toast.error('Failed to download media');
    }
  }, [mediaData?.url, mediaData?.name, mediaData?.extension]);

  const handleMakeVideo = useCallback(() => {
    if (!mediaData?.url || !mediaData.personaId) return;

    // Set the selected persona in the global store
    setSelectedPersona({
      id: mediaData.personaId,
      name: `Persona ${mediaData.personaId}`, // Можно получить реальное имя из контекста
      isMine: true,
    });

    // Save the selected image URL for Video Studio
    setSelectedImage(mediaData.url);

    // Close the modal
    closeMedia();

    // Navigate to Video Studio
    window.location.href = '/video-studio';
  }, [mediaData?.url, mediaData?.personaId, setSelectedPersona, setSelectedImage, closeMedia]);

  // Reset loaded state when media changes
  useEffect(() => {
    setMediaLoaded(false);
  }, [mediaData?.url]);

  if (!mediaData) return null;

  return (
    <ModalPortal isOpen={isOpen} onClose={closeMedia}>
      <div className="relative max-h-[90vh] flex">
        {/* Media Section */}
        <div className="relative">
          {mediaType === 'video' ? (
            <video
              src={mediaData.url}
              className="max-h-[90vh] w-auto object-contain"
              controls
              playsInline
              preload="metadata"
              onLoadedData={() => setMediaLoaded(true)}
              onClick={(e) => e.stopPropagation()}
              style={{ willChange: 'auto' }} // Optimize for performance
            />
          ) : (
            <img
              src={mediaData.url}
              alt={mediaData.name || 'Media'}
              className="max-h-[90vh] w-auto object-contain"
              onLoad={() => setMediaLoaded(true)}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeMedia();
            }}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <Cross2Icon className="w-5 h-5" />
          </button>

          {/* Download Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="absolute top-4 right-16 z-20 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors cursor-pointer"
            aria-label="Download"
          >
            <DownloadIcon className="w-5 h-5" />
          </button>

          {/* Loading state */}
          {!mediaLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
            </div>
          )}
        </div>

        {/* Right Section with Prompt and Actions */}
        <div className="w-[200px] p-4 flex flex-col justify-center">
          {/* Content centered vertically */}
          <div>
            {/* Make Video Button - Only for images */}
            {mediaType === 'image' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMakeVideo();
                }}
                className="w-full px-3 py-2 bg-violet-600 rounded-lg text-white font-medium hover:bg-violet-700 transition-colors cursor-pointer text-sm mb-4"
                aria-label="Make Video"
              >
                Make Video
              </button>
            )}

            {/* Prompt Section */}
            <div>
              {isLoadingInference ? (
                <div className="flex items-center text-xs text-zinc-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500 mr-2" />
                  <span>Loading...</span>
                </div>
              ) : prompt ? (
                <div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyPrompt();
                    }}
                    className="flex items-center text-xs text-violet-400 hover:text-white transition-colors cursor-pointer mb-2"
                    aria-label="Copy Prompt"
                  >
                    <span className="mr-2">Copy prompt</span>
                    <CopyIcon className="w-4 h-4" />
                  </button>
                  <div className="text-xs text-white leading-relaxed break-words">{prompt}</div>
                </div>
              ) : (
                <span className="text-xs text-zinc-500">No prompt available</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
